import React, { useState } from "react";
import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import { deleteService } from "../../api";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";

// DeleteServiceModal — soft-delete confirmation for a service or custom code.
//
// Visual conventions: see /CLAUDE.md "frontend design system" §1.8 Modals
// and §1.8.1 destructive confirmation modals. The service is soft-deleted so
// historical invoices and appointments that reference the ObjectId can still
// resolve the name and price for display.
export default function DeleteServiceModal({
  show,
  onHide,
  service,
  onDeleted = () => {},
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hiddenForSuccess, setHiddenForSuccess] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  const isCustomCode = service?.serviceType === "custom_code";
  const label = isCustomCode ? "Custom Code" : "Service";

  const handleSubmit = async () => {
    if (isSubmitting || !service?._id) return;
    setIsSubmitting(true);
    try {
      await deleteService(service._id);
      showToast("success", `${label} deleted successfully.`);
      setHiddenForSuccess(true);
      onDeleted();
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (error) {
      console.error(`Failed to delete ${label.toLowerCase()}:`, error);
      showToast("error", `Failed to delete ${label.toLowerCase()}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerName = service?.servicesName || "";

  return (
    <>
      <Modal
        show={show && !hiddenForSuccess}
        onHide={onHide}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Delete {label}{headerName ? ` — ${headerName}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="warning" className="mb-0">
            This {label.toLowerCase()} will be{" "}
            <strong>hidden from search, the booking page, and all pickers</strong>{" "}
            so it can no longer be selected on new invoices or appointments.
            Existing invoices and appointments that already use it will keep
            displaying the name and price.
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ minWidth: 140 }}
          >
            {isSubmitting ? (
              <Spinner animation="border" size="sm" role="status" />
            ) : (
              <>
                <i className="fa-solid fa-trash me-2" />
                Delete
              </>
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={isSubmitting}
            style={{ minWidth: 140 }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </>
  );
}
